import {
  Box,
  Button,
  CircularProgress,
  Radio,
  Typography,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import CustomModal from "../../component/common/customModal";
import { InvoiceType } from "../../../types/invoice&payment";
import invoiceService from "../../../services/InvoiceService";
import dashboardServices from "../../../services/DashboardServices";
import CardPaymentForm from "./CardPaymentForm";
import { useTranslation } from "react-i18next";
import { showToast } from "../../component/common/toastNotification";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AssuredWorkloadOutlinedIcon from "@mui/icons-material/AssuredWorkloadOutlined";
import MobileFriendlyOutlinedIcon from "@mui/icons-material/MobileFriendlyOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import { WalletType } from "../../../types/dashboardType";
import { useNavigate } from "react-router-dom";
import { emitPaymentsChanged } from "../../../services/paymentsEvents";
import { buildPaymentCallbackUrl } from "../../../utils/paymentCallbackUrl";
import {
  mapQiCardPaymentError,
  persistAndRedirect,
  startQiCardOrderPayment,
} from "../../../utils/qicardPayFlow";
import {
  linkTransactionToOrder,
  loadActivePaymentSession,
  setActivePaymentSession,
  type ActivePaymentMethod,
} from "../../../utils/abandonedOrderPayments";
import { cancelGatewayPayment, handlePaymentClose } from "../../../utils/paymentCloseCleanup";
import { useCart } from "../../../contexts/CartContext";
import {
  CheckoutError,
  ensureCheckoutOrder,
} from "../../../utils/checkoutOrder";
import {
  OrderPaymentMethod,
  saveCapturedAmount,
  setPaymentInFlight,
} from "../../../utils/checkoutSession";
import { emitCheckoutUiEvent } from "../../../utils/checkoutEvents";
import { formatCheckoutIqd } from "../../../utils/formatCheckoutIqd";
import { runCheckoutGate } from "../../../utils/checkoutGate";
import { NJIM } from "../../../theme/njimTheme";
import codService from "../../../services/CodService";
import { CodSelectedInvoice } from "../../../types/codSalesRep";
import { flattenPayableInvoices, sumInvoiceRemaining } from "../../../utils/codUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  invoiceData: InvoiceType | null;
  checkout?: boolean;
}


const monoFont = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

const primaryBtnSx = {
  background: `${NJIM.walletHero} !important`,
  color: `${NJIM.white} !important`,
  borderRadius: "999px",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 14px rgba(22,51,63,0.35) !important",
  "&:hover": {
    background: `${NJIM.walletHeroHover} !important`,
    boxShadow: "0 6px 18px rgba(22,51,63,0.45) !important",
  },
  "&.Mui-disabled": {
    background: "linear-gradient(118deg, rgba(22,51,63,0.45) 0%, rgba(29,67,86,0.45) 52%, rgba(20,42,53,0.45) 100%) !important",
    color: "rgba(255,255,255,0.55) !important",
    boxShadow: "none !important",
  },
} as const;

function methodToOrderPayment(method: string): number {
  if (method === "mobile") return OrderPaymentMethod.ZainCash;
  if (method === "qicard") return OrderPaymentMethod.QiCard;
  return OrderPaymentMethod.CreditCard;
}

function methodToGatewayType(method: string): ActivePaymentMethod {
  if (method === "mobile") return "ZainCash";
  if (method === "qicard") return "QiCard";
  if (method === "wallet") return "Wallet";
  return "CardPayment";
}

function duplicateStatus(data: Record<string, unknown> | undefined): string {
  return String(data?.paymentStatus ?? data?.existingStatus ?? data?.status ?? "").toUpperCase();
}

const MakePaymentModal: React.FC<Props> = ({
  open,
  onClose,
  invoiceData,
  checkout = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { cart, replaceItems, clearCart } = useCart();
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [CardPaymentModal, setCardPaymentModal] = useState(false);
  const [checkoutId, setCheckoutId] = useState("");
  const [payAmount, setPayAmount] = useState(0);
  const [orderRef, setOrderRef] = useState<{ id?: string; number?: string }>({});
  const [walletBalance, setWalletBalance] = useState<WalletType>({
    balance: 0,
    customerCode: "",
    title: "",
    customerId: "",
  });
  const gatewayRedirectingRef = useRef(false);

  const amount = checkout ? payAmount || cart.total : Number(invoiceData?.amount ?? 0);
  const walletInsufficient = method === "wallet" && walletBalance.balance < amount;

  // COD orders are paid per Sage invoice (POST /payments/cod/invoices/pay), never by order id.
  const isCodPayment =
    !checkout && /^COD-/i.test(String(invoiceData?.orderNumber ?? invoiceData?.invoiceNumber ?? ""));
  const [codInvoices, setCodInvoices] = useState<CodSelectedInvoice[]>([]);

  useEffect(() => {
    if (open) {
      setMethod("card");
      setPayAmount(checkout ? cart.total : Number(invoiceData?.amount ?? 0));
      setWalletLoading(true);
      fetchWalletBalance().finally(() => setWalletLoading(false));
      if (isCodPayment && invoiceData?.id) {
        // This row is either one Sage invoice (pay that invoice) or a COD order (pay all its
        // payable invoices). The amount is the invoices' remainingAmount from the API.
        const rowId = String(invoiceData.id);
        const orderNumber = String(invoiceData.orderNumber ?? "").trim().toLowerCase();
        setCodInvoices([]);
        codService
          .listMyCodInvoices({ includePaid: false })
          .then((orders) => {
            const all = flattenPayableInvoices(orders);
            const single = all.filter((invoice) => invoice.invoiceId === rowId);
            const targets = single.length > 0
              ? single
              : all.filter(
                  (invoice) =>
                    invoice.orderId === rowId ||
                    (orderNumber !== "" &&
                      (invoice.orderNumber.toLowerCase() === orderNumber ||
                        invoice.lineReference.toLowerCase() === orderNumber))
                );
            setCodInvoices(targets);
            setPayAmount(sumInvoiceRemaining(targets));
          })
          .catch(() => setCodInvoices([]));
      } else if (!checkout && invoiceData?.id) {
        invoiceService
          .getOrderPaymentSummary(String(invoiceData.id))
          .then((res) => {
            const summary = (res.data ?? {}) as {
              remainingAmount?: number;
              paymentStatus?: string;
              pendingTransactionId?: string;
              orderNumber?: string;
            };
            const remaining = Number(summary.remainingAmount);
            if (Number.isFinite(remaining) && remaining >= 0) {
              setPayAmount(remaining);
            }
            const status = String(summary.paymentStatus ?? "").toUpperCase();
            if (status.includes("PAID") && !status.includes("PARTIAL") && remaining <= 0) {
              showToast(
                t("salesOrder.orderAlreadyPaid", {
                  defaultValue: "This order is already paid.",
                }),
                "info"
              );
            }
            if (summary.orderNumber) {
              setOrderRef({
                id: String(invoiceData.id),
                number: summary.orderNumber,
              });
            }
          })
          .catch(() => {
            /* keep invoice amount */
          });
      }
    }
  }, [open, checkout, cart.total, invoiceData?.amount, invoiceData?.id, invoiceData?.orderNumber, isCodPayment, t]);

  useEffect(() => {
    const orderId = String(invoiceData?.id ?? orderRef.id ?? "");
    if (!open || !orderId) return;
    setActivePaymentSession({
      orderId,
      type: methodToGatewayType(method),
      transactionId: checkoutId || undefined,
    });
  }, [open, invoiceData?.id, orderRef.id, method, checkoutId]);

  const fetchWalletBalance = async () => {
    try {
      const response = await dashboardServices.getWalletBalance();
      const finalResponse = response?.data;
      if (finalResponse) setWalletBalance(finalResponse);
    } catch (error) {
      console.error("Error fetching wallet balance:", error);
    }
  };

  const finishSuccess = (opts: {
    orderId?: string;
    orderNumber?: string;
    transactionId?: string;
    landOn: "step1" | "history";
  }) => {
    if (checkout) {
      clearCart();
      emitCheckoutUiEvent({
        type: "success",
        amount,
        orderId: opts.orderId,
        orderNumber: opts.orderNumber,
        transactionId: opts.transactionId,
        landOn: opts.landOn,
      });
    }
    emitPaymentsChanged();
    showToast(t("invoicesPayments.paymentSuccessful"), "success");
    if (checkout && opts.landOn === "history") {
      navigate("/saleOrder?tab=OrderHistory");
    }
    onClose();
  };

  const handleDuplicate = async (
    data: Record<string, unknown>,
    chosenType: string,
    orderId: string
  ): Promise<boolean> => {
    const status = duplicateStatus(data);
    const txId = String(data.transactionId ?? data.existingTransactionId ?? "");
    const existingType = String(data.type ?? data.existingType ?? chosenType);

    if (status.includes("SUCCESS") || status.includes("PAID")) {
      finishSuccess({
        orderId,
        orderNumber: orderRef.number,
        transactionId: txId,
        landOn: "step1",
      });
      return true;
    }

    if (chosenType === "ZainCash" && existingType && existingType !== "ZainCash") {
      showToast(
        t("salesOrder.zainBlockedOtherGateway", {
          defaultValue: "Start a new order to pay with ZainCash.",
        }),
        "info"
      );
      return true;
    }

    if (txId) {
      showToast(
        t("salesOrder.paymentInProgress", {
          defaultValue: "An existing payment is still processing.",
        }),
        "info"
      );
      if (chosenType === "QiCard") {
        await startQiCardOrderPayment({
          orderId,
          amount,
          language: i18n.language,
        });
        return true;
      }
      navigate(
        checkout
          ? `/saleOrder?checkout=1&paymentStatus&type=${existingType || chosenType}&id=${txId}`
          : `/saleOrder?tab=OrderHistory&paymentStatus&type=${existingType || chosenType}&id=${txId}`
      );
      onClose();
      return true;
    }
    return false;
  };

  const handleProceedToPayment = async () => {
    if (method === "bank") return;
    if (walletInsufficient) {
      showToast(t("invoicesPayments.insufficientBalance"), "error");
      return;
    }

    setLoading(true);
    setPaymentInFlight(true);
    saveCapturedAmount(amount);

    try {
      let orderId = String(invoiceData?.id ?? "");
      let invoiceId = String(invoiceData?.receiptInvoiceId ?? "");
      let orderNumber = String(invoiceData?.orderNumber ?? "");

      if (checkout) {
        const gate = await runCheckoutGate(cart.items, replaceItems);
        if (!gate.ok) {
          if (gate.reason === "changed") {
            showToast(
              t("salesOrder.cartChangedReview", {
                defaultValue: "Prices or stock changed. Review your cart.",
              }),
              "info"
            );
          } else if (gate.reason === "validate_failed") {
            showToast(
              t("salesOrder.unableToValidateStock", {
                defaultValue: "Unable to validate stock. Please try again.",
              }),
              "error"
            );
          } else if (gate.reason === "empty_after_update") {
            showToast(
              t("salesOrder.cartEmptyAfterStock", {
                defaultValue: "Cart is empty after stock update.",
              }),
              "info"
            );
          } else if (gate.reason === "invalid_price") {
            showToast(
              t("salesOrder.invalidPricePopup", {
                defaultValue: "This product has IQD 0.00 price and cannot be ordered.",
              }),
              "error"
            );
          }
          return;
        }

        const held = await ensureCheckoutOrder({
          cart: {
            ...cart,
            items: gate.items,
          },
          paymentMethod: methodToOrderPayment(method),
        });
        orderId = held.orderId;
        invoiceId = held.invoiceId ?? "";
        orderNumber = held.orderNumber ?? "";
        setOrderRef({ id: orderId, number: orderNumber });
        if (held.invoiceFailed) {
          showToast(
            t("salesOrder.invoiceCreateFailed", {
              defaultValue: "Invoice could not be created. Payment will continue.",
            }),
            "info"
          );
        }
      }

      if (!orderId) {
        showToast(t("errors.failedToSubmitOrder"), "error");
        return;
      }

      const gatewayType = methodToGatewayType(method);
      const historyReturn = `${window.location.origin}/saleOrder?tab=OrderHistory&paymentStatus&type=${gatewayType}`;
      const checkoutReturn = `${window.location.origin}/saleOrder?checkout=1&paymentStatus&type=${gatewayType}`;
      const returnUrl = checkout ? checkoutReturn : historyReturn;

      if (method === "qicard" && checkout) {
        try {
          gatewayRedirectingRef.current = true;
          await startQiCardOrderPayment({
            orderId,
            amount,
            language: i18n.language,
          });
        } catch (err) {
          gatewayRedirectingRef.current = false;
          const code = mapQiCardPaymentError(err);
          showToast(
            code === "missing_redirect"
              ? t("qicard.missingRedirect")
              : t("invoicesPayments.paymentFailed"),
            "error"
          );
        }
        return;
      }

      if (isCodPayment) {
        if (gatewayType === "Wallet") {
          showToast(t("invoicesPayments.codWalletNotSupported"), "error");
          return;
        }
        if (codInvoices.length === 0) {
          showToast(t("invoicesPayments.codNoPayableInvoice"), "info");
          return;
        }
        if (gatewayType === "ZainCash" && codInvoices.some((invoice) => !Number.isInteger(invoice.remainingAmount))) {
          showToast(t("invoicesPayments.codWholeIqdRequired"), "error");
          return;
        }
      }

      const requestPayment = async (): Promise<Record<string, unknown>> => {
        if (isCodPayment) {
          return codService.payMyCodInvoices({
            invoices: codInvoices.map((invoice) => ({ invoiceId: invoice.invoiceId })),
            method: gatewayType,
            language: method === "mobile" ? "ar" : "en",
            externalReferenceId: "web",
            redirectUrl: historyReturn,
            successUrl: historyReturn,
            failedUrl: historyReturn,
          });
        }
        if (checkout) {
          const inputData: Record<string, unknown> = {
            orderId,
            amount,
            language: method === "mobile" ? "ar" : "en",
            externalReferenceId: "web",
            type: gatewayType,
          };
          if (invoiceId) inputData.invoiceId = invoiceId;
          if (method !== "wallet") {
            inputData.redirectUrl = returnUrl;
            inputData.successUrl = returnUrl;
            inputData.failedUrl = returnUrl;
          }
          const response = await invoiceService.PaymentCreate(inputData);
          return (response.data ?? {}) as Record<string, unknown>;
        }
        const payBody: Parameters<typeof invoiceService.payOrder>[1] = {
          payFullRemaining: true,
          method: gatewayType,
          language: method === "mobile" ? "ar" : "en",
          externalReferenceId: "web",
        };
        if (method !== "wallet") {
          payBody.redirectUrl = historyReturn;
          payBody.successUrl = historyReturn;
          payBody.failedUrl = historyReturn;
        }
        const response = await invoiceService.payOrder(orderId, payBody);
        return (response.data ?? {}) as Record<string, unknown>;
      };

      let data = await requestPayment();

      // An earlier card attempt on this order is still PENDING (card form closed, page
      // refreshed, quick retry…). Its checkout was never paid, so checking its status only
      // returns HyperPay's "No payment session found" → "card payment session expired".
      // Release it on the server (the API checks HyperPay first and books it if it WAS paid)
      // and start a fresh checkout — the same outcome the mobile app gets.
      if (data.isDuplicate && gatewayType === "CardPayment") {
        const staleTxId = String(data.transactionId ?? data.existingTransactionId ?? "");
        const staleStatus = duplicateStatus(data);
        if (staleTxId && !staleStatus.includes("SUCCESS") && !staleStatus.includes("PAID")) {
          const outcome = await cancelGatewayPayment(staleTxId);
          if (outcome === "completed") {
            finishSuccess({ orderId, orderNumber, transactionId: staleTxId, landOn: "step1" });
            return;
          }
          if (outcome === "canceled") {
            if (localStorage.getItem("lastCardPaymentTransactionId") === staleTxId) {
              localStorage.removeItem("lastCardPaymentTransactionId");
            }
            data = await requestPayment();
          }
        }
      }

      const txId = String(data.transactionId ?? data.checkoutId ?? "");
      if (txId) linkTransactionToOrder(txId, orderId);
      setActivePaymentSession({
        orderId,
        type: gatewayType,
        transactionId: txId || undefined,
      });

      if (data.isDuplicate) {
        await handleDuplicate(data, gatewayType, orderId);
        return;
      }

      if (method === "wallet") {
        finishSuccess({
          orderId,
          orderNumber,
          transactionId: txId,
          landOn: "step1",
        });
        return;
      }

      if (method === "qicard") {
        try {
          gatewayRedirectingRef.current = true;
          persistAndRedirect(String(data.redirectUrl ?? ""), {
            transactionId: txId,
            kind: "order",
            orderId,
            amount,
          });
        } catch (err) {
          gatewayRedirectingRef.current = false;
          const code = mapQiCardPaymentError(err);
          showToast(
            code === "missing_redirect"
              ? t("qicard.missingRedirect")
              : t("invoicesPayments.paymentFailed"),
            "error"
          );
        }
        return;
      }

      if (data.redirectUrl) {
        gatewayRedirectingRef.current = true;
        if (gatewayType === "ZainCash" && txId) {
          localStorage.setItem("lastZainCashTransactionId", txId);
        }
        window.location.href = String(data.redirectUrl);
        return;
      }

      if (method === "card" && txId) {
        localStorage.setItem("lastCardPaymentTransactionId", txId);
        setCheckoutId(txId);
        setCardPaymentModal(true);
        return;
      }

      showToast(t("invoicesPayments.paymentFailed"), "error");
    } catch (err) {
      if (err instanceof CheckoutError) {
        if (err.code === "NotAuthorizedForItem" || err.code === "ConsumptionExceeded") {
          const name =
            cart.items.find((l) => {
              const code = (err as CheckoutError).productCode;
              return l.product.code === code || l.product.id === code;
            })?.product.name ?? (err as CheckoutError).productCode;
          showToast(
            err.code === "ConsumptionExceeded"
              ? t("salesOrder.itemExceedsAllowance", {
                  product: name,
                  defaultValue: "{{product}} exceeds your allowance",
                })
              : t("salesOrder.itemNotAvailableForAccount", {
                  product: name,
                  defaultValue: "{{product}} is not available for your account",
                }),
            "error"
          );
          return;
        }
        if (err.code === "401") {
          showToast(t("errors.sessionExpired"), "error");
          return;
        }
      }
      const axiosErr = err as {
        response?: { status?: number; data?: { error?: string; code?: string; message?: string } };
      };
      const code = String(axiosErr.response?.data?.code ?? axiosErr.response?.data?.error ?? "");
      if (code === "ORDER_ALREADY_PAID") {
        showToast(
          t("salesOrder.orderAlreadyPaid", {
            defaultValue: "This order is already paid.",
          }),
          "info"
        );
        return;
      }
      if (code === "AMOUNT_EXCEEDS_BALANCE") {
        showToast(
          t("salesOrder.amountExceedsBalance", {
            defaultValue: "This amount exceeds the remaining balance.",
          }),
          "error"
        );
        return;
      }
      if (code === "INSUFFICIENT_WALLET_BALANCE") {
        showToast(t("invoicesPayments.insufficientBalance"), "error");
        return;
      }
      if (code === "PAYMENT_IN_PROGRESS" || axiosErr.response?.status === 409) {
        const d = (axiosErr.response?.data ?? {}) as Record<string, unknown>;
        await handleDuplicate(
          { ...d, isDuplicate: true },
          methodToGatewayType(method),
          String(invoiceData?.id ?? orderRef.id ?? "")
        );
        return;
      }
      showToast(
        axiosErr.response?.data?.message ||
          axiosErr.response?.data?.error ||
          t("invoicesPayments.paymentFailed"),
        "error"
      );
    } finally {
      setLoading(false);
      setPaymentInFlight(false);
    }
  };

  const paymentOptions = [
    {
      label: t("invoicesPayments.creditDebitCard"),
      sub: t("invoicesPayments.payWithCard"),
      value: "card",
      icon: CreditCardOutlinedIcon,
    },
    {
      label: t("invoicesPayments.qiCard"),
      sub: t("invoicesPayments.payWithQiCard"),
      value: "qicard",
      icon: PaymentOutlinedIcon,
    },
    {
      label: t("invoicesPayments.mobileWallet"),
      sub: t("invoicesPayments.payViaMobileWallet"),
      value: "mobile",
      icon: MobileFriendlyOutlinedIcon,
    },
    {
      label: t("invoicesPayments.prepaymentWallet"),
      sub: walletLoading
        ? t("salesOrder.checkingBalance", { defaultValue: "Checking balance…" })
        : `${t("invoicesPayments.availableBalance")}: ${formatCheckoutIqd(walletBalance.balance)}`,
      value: "wallet",
      icon: AccountBalanceWalletOutlinedIcon,
      disabled: isCodPayment || (walletInsufficient && !walletLoading),
    },
    {
      label: t("invoicesPayments.bankTransfer"),
      sub: t("invoicesPayments.comingSoon", { defaultValue: "Soon" }),
      value: "bank",
      disabled: true,
      icon: AssuredWorkloadOutlinedIcon,
    },
  ];

  const proceedLabel = `${t("invoicesPayments.proceedToPayment")} · ${formatCheckoutIqd(amount)}`;
  const payDisabled =
    loading ||
    !method ||
    method === "bank" ||
    walletInsufficient ||
    walletLoading ||
    amount <= 0;

  const handleCardFormClose = (outcome?: "canceled" | "completed" | "still_processing" | "failed") => {
    setCardPaymentModal(false);
    setCheckoutId("");
    emitCheckoutUiEvent({ type: "keep_review" });
    if (outcome === "completed") {
      showToast(t("invoicesPayments.paymentSuccessful"), "success");
      return;
    }
    if (outcome === "still_processing") {
      showToast(
        t("salesOrder.paymentStillWithProvider", {
          defaultValue:
            "The payment provider is still processing this payment. Please wait for it to finish.",
        }),
        "info"
      );
      return;
    }
    if (outcome === "failed") {
      showToast(
        t("salesOrder.paymentCancelFailed", {
          defaultValue: "Could not cancel this payment. Please try again.",
        }),
        "error"
      );
      return;
    }
    showToast(
      t("salesOrder.cardCancelled", { defaultValue: "Payment cancelled" }),
      "info"
    );
  };

  const handleModalClose = async () => {
    if (checkout && gatewayRedirectingRef.current) {
      onClose();
      return;
    }

    const session = loadActivePaymentSession();
    if (session && !session.completed) {
      if (CardPaymentModal && checkoutId) {
        await handlePaymentClose({
          ...session,
          type: "CardPayment",
          transactionId: checkoutId,
        });
      } else {
        await handlePaymentClose(session);
      }
    }

    setCardPaymentModal(false);
    setCheckoutId("");
    onClose();
  };

  const paymentRedirectUrl = checkoutId
    ? buildPaymentCallbackUrl({
        returnTo: "saleOrder",
        type: "CardPayment",
        checkoutId,
      })
    : buildPaymentCallbackUrl({ returnTo: "saleOrder", type: "CardPayment" });

  return (
    <>
      <CustomModal
        open={open}
        onClose={handleModalClose}
        ModalTitle={t("invoicesPayments.makePayment")}
        ModalDescription={t("invoicesPayments.selectPaymentMethod")}
        width={{ xs: "94%", sm: 560, md: 620 }}
        bgcolor={NJIM.white}
        paperSx={{
          p: { xs: 3, sm: 4 },
          borderRadius: "16px",
          border: `1px solid ${NJIM.border}`,
          boxShadow: NJIM.elevation,
          maxWidth: "100%",
        }}
        titleSx={{
          fontSize: { xs: 20, sm: 22 },
          fontWeight: 600,
          color: NJIM.textPrimary,
          letterSpacing: "-0.02em",
        }}
        descriptionSx={{
          fontSize: { xs: 14, sm: 15 },
          color: NJIM.textSecondary,
          mt: 0.75,
          mb: 0.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, sm: 3.5 }, mt: 3 }}>
          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              bgcolor: NJIM.muted,
              borderRadius: "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: NJIM.textSecondary }}>
                {t("invoicesPayments.balance")}:
              </Typography>
              <Typography
                sx={{
                  fontFamily: monoFont,
                  fontSize: { xs: 18, sm: 22 },
                  fontWeight: 600,
                  color: NJIM.mintForeground,
                  direction: "ltr",
                  unicodeBidi: "isolate",
                }}
              >
                {formatCheckoutIqd(amount)}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mt: 1.25, pt: 1.25, borderTop: `1px solid ${NJIM.border}` }}>
              {t("salesOrder.cancelPaymentHint", {
                defaultValue: "You can cancel the payment and choose another method.",
              })}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: { xs: 15, sm: 16 },
                color: NJIM.textPrimary,
                mb: 1.75,
              }}
            >
              {t("invoicesPayments.selectPaymentMethodTitle")}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {paymentOptions.map((item) => {
                const Icon = item.icon;
                const isSelected = method === item.value;
                const disabled = Boolean(item.disabled);
                return (
                  <Box
                    key={item.value}
                    onClick={() => !disabled && setMethod(item.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.75,
                      p: { xs: 1.75, sm: 2 },
                      borderRadius: "12px",
                      border: `1px solid ${isSelected ? NJIM.mint : NJIM.border}`,
                      bgcolor: isSelected ? NJIM.mintWash : NJIM.white,
                      cursor: disabled ? "default" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                      transition: "background-color 180ms ease, border-color 180ms ease",
                      "&:hover": {
                        bgcolor: disabled ? undefined : NJIM.muted,
                        borderColor: disabled ? undefined : NJIM.mint,
                      },
                    }}
                  >
                    <Radio
                      checked={isSelected}
                      onChange={() => !disabled && setMethod(item.value)}
                      disabled={disabled}
                      size="medium"
                      sx={{
                        p: 0.5,
                        color: NJIM.border,
                        "&.Mui-checked": { color: NJIM.mint },
                      }}
                    />
                    <Icon sx={{ fontSize: 22, color: isSelected ? NJIM.ink : NJIM.textSecondary }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: NJIM.textPrimary }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: NJIM.textSecondary, lineHeight: 1.3 }}>
                        {item.sub}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Button
            className="njim-primary-btn"
            variant="contained"
            fullWidth
            disableElevation
            disableRipple
            color="inherit"
            disabled={payDisabled}
            onClick={handleProceedToPayment}
            sx={{
              ...primaryBtnSx,
              py: { xs: 1.5, sm: 1.75 },
              fontSize: { xs: 15, sm: 16 },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: NJIM.white }} /> : proceedLabel}
          </Button>
        </Box>
      </CustomModal>

      {CardPaymentModal && (
        <CardPaymentForm
          open={CardPaymentModal}
          onClose={handleCardFormClose}
          checkoutId={checkoutId}
          orderId={String(invoiceData?.id ?? orderRef.id ?? "") || undefined}
          action={paymentRedirectUrl}
          amount={amount}
          currency="IQD"
        />
      )}
    </>
  );
};

export default MakePaymentModal;
